import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  Stack,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  FormControlLabel,
  Switch,
  IconButton,
  TextField,
  Divider,
  LinearProgress,
  Avatar,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Replay as ReplayIcon,
  History as HistoryIcon,
  OpenInNew as OpenInNewIcon,
  LayersClear as LayersClearIcon,
} from '@mui/icons-material';
import api from '../api';
import { useAuth } from '../AuthContext';
import {
  explorerAddressHref,
  explorerSiteLabel,
  explorerSourceTokenHref,
  explorerTxHref,
  MINT_EXECUTOR_CHAIN_ID,
  shorten0x,
  txUrl,
} from '../utils/explorer';

const STATUSES = [
  'received',
  'eligible',
  'mint_submitted',
  'mint_confirmed',
  'rejected_orphan',
  'failed',
];

const STATUS_COLOR = {
  received: 'default',
  eligible: 'info',
  mint_submitted: 'warning',
  mint_confirmed: 'success',
  rejected_orphan: 'default',
  failed: 'error',
};

function chainLabel(chainId) {
  if (chainId === null || chainId === undefined) {
    return 'No chain';
  }
  return String(chainId);
}

const CHAIN_NAMES = {
  43114: 'Avalanche C-Chain',
  13371: 'Immutable zkEVM',
  15135: 'Paradise L1',
};

function chainName(chainId) {
  if (chainId == null) return 'No chain';
  const n = Number(chainId);
  return CHAIN_NAMES[n] ? `${CHAIN_NAMES[n]} (${n})` : String(n);
}

const REWIND_PRESETS = [
  { label: '~1h (1,800 blocks)', blocks: 1800 },
  { label: '~6h (10,800 blocks)', blocks: 10800 },
  { label: '~24h (43,200 blocks)', blocks: 43200 },
  { label: '~3d (129,600 blocks)', blocks: 129600 },
  { label: '~7d (302,400 blocks)', blocks: 302400 },
];

function formatDuration(totalSeconds) {
  if (totalSeconds == null || !Number.isFinite(totalSeconds)) return '—';
  if (totalSeconds < 1) return '<1s';
  const s = Math.round(totalSeconds);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const sec = s % 60;
  if (m < 60) return `${m}m ${sec}s`;
  const h = Math.floor(m / 60);
  const min = m % 60;
  if (h < 24) return `${h}h ${min}m`;
  const d = Math.floor(h / 24);
  const hr = h % 24;
  return `${d}d ${hr}h`;
}

function formatHeartbeatAge(iso) {
  if (!iso) return '—';
  const ageMs = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ageMs) || ageMs < 0) return '—';
  return `${formatDuration(ageMs / 1000)} ago`;
}

function formatCell(value) {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') {
    const s = JSON.stringify(value);
    return s.length > 80 ? `${s.slice(0, 80)}…` : s;
  }
  const str = String(value);
  return str.length > 64 ? `${str.slice(0, 64)}…` : str;
}

const MINT_TABLE_COL_COUNT = 7;

function MetadataPreview({ name, imageUrl, compact }) {
  const n = name != null && String(name).trim() !== '' ? String(name).trim() : null;
  const u = imageUrl != null && String(imageUrl).trim() !== '' ? String(imageUrl).trim() : null;
  const show = n || u;
  const maxName = compact ? 28 : 48;
  const maxW = compact ? 130 : 220;
  const av = compact ? 28 : 40;
  if (!show) {
    return (
      <Typography variant="caption" color="text.secondary">
        —
      </Typography>
    );
  }
  const nameShort = n && n.length > maxName ? `${n.slice(0, maxName)}…` : n;
  return (
    <Tooltip title={[n, u].filter(Boolean).join('\n') || ''}>
      <Stack direction="row" spacing={compact ? 0.5 : 1} alignItems="center" sx={{ maxWidth: maxW }}>
        {u ? (
          <Avatar
            variant="rounded"
            src={u}
            alt=""
            sx={{ width: av, height: av }}
            imgProps={{
              onError: (e) => {
                e.currentTarget.style.display = 'none';
              },
            }}
          />
        ) : null}
        <Typography variant="caption" sx={{ lineHeight: 1.2 }}>
          {nameShort || '(no name)'}
        </Typography>
      </Stack>
    </Tooltip>
  );
}

/**
 * Link to minted V2 NFT on Avalanche (mint executor chain), not MintPipelineRequests.chainId (burn chain).
 */
function V2MintedTokenExplorerLink({ contract, tokenId }) {
  const addrOk = contract != null && String(contract).trim() !== '';
  const idOk = tokenId != null && String(tokenId).trim() !== '';
  if (!addrOk || !idOk) return null;
  const href = explorerSourceTokenHref(MINT_EXECUTOR_CHAIN_ID, contract, tokenId);
  if (!href) return null;
  return (
    <Tooltip title="View V2 token on Avalanche">
      <IconButton
        size="small"
        component="a"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ p: 0.25 }}
        aria-label="View V2 token on Avalanche"
      >
        <OpenInNewIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Tooltip>
  );
}

/**
 * Link to legacy/source NFT on explorers for MintPipelineRequests.chainId (burn chain; null for gameplay).
 */
function SourceLegacyTokenExplorerLink({ sourceChainId, sourceContract, sourceTokenId }) {
  const addrOk = sourceContract != null && String(sourceContract).trim() !== '';
  const idOk = sourceTokenId != null && String(sourceTokenId).trim() !== '';
  if (!addrOk || !idOk) return null;
  const href = explorerSourceTokenHref(sourceChainId, sourceContract, sourceTokenId);
  let disabledReason =
    sourceChainId == null || sourceChainId === ''
      ? 'Legacy chain unknown for this row — cannot open source token explorer.'
      : null;
  if (!disabledReason && !href) {
    const n = Number(sourceChainId);
    disabledReason = Number.isFinite(n)
      ? `No NFT/token explorer URL mapped for chain ${n}.`
      : 'Legacy chain unknown — cannot open source token explorer.';
  }

  if (href) {
    return (
      <Tooltip title="View source token">
        <IconButton
          size="small"
          component="a"
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ p: 0.25 }}
          aria-label="View source token"
        >
          <OpenInNewIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    );
  }
  return (
    <Tooltip title={disabledReason}>
      <span>
        <IconButton size="small" disabled sx={{ p: 0.25 }} aria-label="View source token (unavailable)">
          <OpenInNewIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </span>
    </Tooltip>
  );
}

/**
 * Shortened 0x value with optional explorer icon when chainId has a configured explorer.
 */
function ExplorerHexLink({ chainId, kind, raw, label, iconTooltip }) {
  if (raw == null || String(raw).trim() === '') return null;
  const norm = String(raw).trim().toLowerCase();
  const display = shorten0x(raw);
  const base = kind === 'address' ? explorerAddressHref(chainId) : null;
  const looksHex = /^0x[0-9a-f]+$/.test(norm);
  const href =
    kind === 'tx' ? txUrl(chainId, raw) : base && looksHex ? `${base}/${norm}` : null;

  let explorerHint = '';
  if (chainId == null || chainId === '') {
    explorerHint = 'No chainId on this row — explorer link unavailable.';
  } else if (kind === 'address' && !base) {
    explorerHint = `No block explorer configured for chain ${chainId}.`;
  } else if (kind === 'tx' && !explorerTxHref(chainId)) {
    explorerHint = `No block explorer configured for chain ${chainId}.`;
  } else if (!looksHex) {
    explorerHint = 'Value is not a standard 0x hex — no explorer link.';
  }

  const site = explorerSiteLabel(chainId);
  const tip = [String(raw).trim(), href ? `${site}\n${href}` : explorerHint || site].filter(Boolean).join('\n\n');

  return (
    <Stack direction="row" alignItems="center" spacing={0.25} sx={{ flexWrap: 'wrap', maxWidth: 280 }}>
      {label ? (
        <Typography variant="caption" color="text.secondary" component="span">
          {label}
        </Typography>
      ) : null}
      <Tooltip title={tip}>
        <Typography variant="caption" component="span" sx={{ fontFamily: 'monospace', cursor: 'default' }}>
          {display}
        </Typography>
      </Tooltip>
      {href ? (
        <Tooltip title={iconTooltip || 'Open in block explorer'}>
          <IconButton
            size="small"
            component="a"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ p: 0.25 }}
            aria-label={iconTooltip || 'Open in block explorer'}
          >
            <OpenInNewIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      ) : null}
    </Stack>
  );
}

const MINT_PIPELINE_ROLES = ['manager', 'admin', 'super_admin'];
const REQUEUE_COOLDOWN_MS = 20_000;

export default function MintPipelineManager() {
  const { user } = useAuth();
  const canMutatePipeline = user?.role === 'super_admin';
  const canRequeueFailed = MINT_PIPELINE_ROLES.includes(user?.role);
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [chainFilter, setChainFilter] = useState('');
  const [contractFilter, setContractFilter] = useState('');
  const [tokenIdFilter, setTokenIdFilter] = useState('');

  const { data: stats, isLoading: statsLoading, isError: statsError, error: statsErr, refetch: refetchStats } = useQuery({
    queryKey: ['mintPipelineStats'],
    queryFn: async () => {
      const response = await api.get('/mint-pipeline/stats');
      return response.data;
    },
    staleTime: 30_000,
  });

  const { data: settings, isLoading: settingsLoading } = useQuery({
    queryKey: ['mintPipelineSettings'],
    queryFn: async () => {
      const response = await api.get('/mint-pipeline/settings');
      return response.data;
    },
    staleTime: 15_000,
  });

  const { data: contractsData, isError: contractsError, error: contractsErr } = useQuery({
    queryKey: ['mintPipelineContracts'],
    queryFn: async () => {
      const response = await api.get('/mint-pipeline/contracts');
      return response.data;
    },
    staleTime: 60_000,
  });

  const contractOptions = contractsData?.addresses ?? [];

  const effectiveContractFilter = useMemo(() => {
    const t = contractFilter.trim();
    if (!t) return '';
    return contractOptions.includes(t) ? t : '';
  }, [contractFilter, contractOptions]);

  useEffect(() => {
    const t = contractFilter.trim();
    if (!t) return;
    if (contractOptions.length > 0 && !contractOptions.includes(t)) {
      setContractFilter('');
      setPage(0);
    }
  }, [contractFilter, contractOptions]);

  const queryKeyRows = useMemo(
    () =>
      [
        'mintPipelineRows',
        page + 1,
        rowsPerPage,
        statusFilter,
        sourceFilter,
        chainFilter,
        effectiveContractFilter,
        tokenIdFilter,
      ],
    [page, rowsPerPage, statusFilter, sourceFilter, chainFilter, effectiveContractFilter, tokenIdFilter]
  );

  const { data: health, refetch: refetchHealth, isFetching: healthFetching } = useQuery({
    queryKey: ['mintPipelineHealth'],
    queryFn: async () => {
      const response = await api.get('/mint-pipeline/health');
      return response.data;
    },
    staleTime: 5_000,
    refetchInterval: 10_000,
  });

  const [rewindChainId, setRewindChainId] = useState('');
  const [rewindBlocks, setRewindBlocks] = useState(43200);
  const [requeueCooldownIds, setRequeueCooldownIds] = useState({});
  const [bulkRequeueCooldown, setBulkRequeueCooldown] = useState(false);

  const rewindMutation = useMutation({
    mutationFn: async ({ chainId, blocks }) => {
      const response = await api.post('/mint-pipeline/rewind-cursor', { chainId, blocks });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mintPipelineHealth'] });
    },
  });

  const pauseMutation = useMutation({
    mutationFn: async (mintingPaused) => {
      const response = await api.patch('/mint-pipeline/settings', { mintingPaused });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mintPipelineSettings'] });
      queryClient.invalidateQueries({ queryKey: ['mintPipelineHealth'] });
    },
  });

  const requeueMutation = useMutation({
    mutationFn: async (id) => {
      const response = await api.post('/mint-pipeline/requeue-failed', { id });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mintPipelineStats'] });
      queryClient.invalidateQueries({ queryKey: ['mintPipelineRows'] });
    },
  });

  const markRequeueCooldownMany = (ids) => {
    const unique = [...new Set(ids.filter(Boolean))];
    if (!unique.length) return;
    setRequeueCooldownIds((prev) => {
      const next = { ...prev };
      unique.forEach((id) => {
        next[id] = true;
      });
      return next;
    });
    unique.forEach((id) => {
      window.setTimeout(() => {
        setRequeueCooldownIds((prev) => {
          if (!prev[id]) return prev;
          const next = { ...prev };
          delete next[id];
          return next;
        });
      }, REQUEUE_COOLDOWN_MS);
    });
  };

  const markRequeueCooldown = (id) => markRequeueCooldownMany([id]);

  const bulkRequeueMutation = useMutation({
    mutationFn: async (ids) => {
      let ok = 0;
      let failed = 0;
      for (const id of ids) {
        try {
          await api.post('/mint-pipeline/requeue-failed', { id });
          ok += 1;
        } catch {
          failed += 1;
        }
      }
      return { ok, failed, total: ids.length };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mintPipelineStats'] });
      queryClient.invalidateQueries({ queryKey: ['mintPipelineRows'] });
    },
  });

  const isRequeueDisabled = (id) =>
    !!requeueCooldownIds[id] ||
    bulkRequeueMutation.isPending ||
    (requeueMutation.isPending && requeueMutation.variables === id);

  const handleRequeue = (id) => {
    if (isRequeueDisabled(id)) return;
    markRequeueCooldown(id);
    requeueMutation.mutate(id);
  };

  const clearLegacyCacheMutation = useMutation({
    mutationFn: async (id) => {
      const response = await api.post('/mint-pipeline/clear-legacy-metadata-cache', { id });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mintPipelineRows'] });
    },
  });

  const {
    data: rowsData,
    isLoading: rowsLoading,
    isError: rowsError,
    error: rowsErr,
    refetch: refetchRows,
  } = useQuery({
    queryKey: queryKeyRows,
    queryFn: async () => {
      const params = new URLSearchParams({
        page: String(page + 1),
        limit: String(rowsPerPage),
      });
      if (statusFilter) params.set('status', statusFilter);
      if (sourceFilter) params.set('source', sourceFilter);
      if (chainFilter) params.set('chainId', chainFilter);
      if (effectiveContractFilter) params.set('contract', effectiveContractFilter);
      if (tokenIdFilter.trim()) params.set('tokenId', tokenIdFilter.trim());
      const response = await api.get(`/mint-pipeline/rows?${params}`);
      return response.data;
    },
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });

  const handleRefresh = () => {
    refetchStats();
    refetchRows();
    refetchHealth();
    queryClient.invalidateQueries({ queryKey: ['mintPipelineContracts'] });
  };

  const rows = rowsData?.data ?? [];
  const pagination = rowsData?.pagination;

  const visibleRequeueableIds = useMemo(() => {
    if (!canRequeueFailed) return [];
    return rows
      .filter((row) => row.status === 'failed' && !requeueCooldownIds[row.id])
      .map((row) => row.id);
  }, [rows, canRequeueFailed, requeueCooldownIds]);

  const handleBulkRequeue = () => {
    const ids = visibleRequeueableIds;
    if (!ids.length || bulkRequeueCooldown || bulkRequeueMutation.isPending) return;
    setBulkRequeueCooldown(true);
    window.setTimeout(() => setBulkRequeueCooldown(false), REQUEUE_COOLDOWN_MS);
    markRequeueCooldownMany(ids);
    bulkRequeueMutation.mutate(ids);
  };

  if (!MINT_PIPELINE_ROLES.includes(user?.role)) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">This page requires manager, admin, or super_admin.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }} flexWrap="wrap" gap={1}>
        <Typography variant="h5" component="h1">
          Mint pipeline
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1}>
          {canMutatePipeline ? (
            <FormControlLabel
              control={
                <Switch
                  checked={!!settings?.mintingPaused}
                  disabled={settingsLoading || pauseMutation.isPending}
                  onChange={(_, v) => pauseMutation.mutate(v)}
                />
              }
              label="Minting paused"
            />
          ) : settings?.mintingPaused ? (
            <Chip size="small" color="warning" label="Minting paused" />
          ) : null}
          <Button startIcon={<RefreshIcon />} onClick={handleRefresh} variant="outlined" size="small">
            Refresh
          </Button>
        </Stack>
      </Stack>

      {(statsError || rowsError || contractsError) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {statsError ? statsErr?.message : rowsError ? rowsErr?.message : contractsErr?.message}
        </Alert>
      )}

      {clearLegacyCacheMutation.isError ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => clearLegacyCacheMutation.reset()}>
          {clearLegacyCacheMutation.error?.response?.data?.error ||
            clearLegacyCacheMutation.error?.message ||
            'Clear legacy cache failed'}
        </Alert>
      ) : null}

      {clearLegacyCacheMutation.isSuccess && clearLegacyCacheMutation.data ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => clearLegacyCacheMutation.reset()}>
          Removed {clearLegacyCacheMutation.data.deletedRows} LegacyMetadataCaches row(s) for keys:{' '}
          {(clearLegacyCacheMutation.data.sourceTokenIdsRemoved || []).join(', ')}
        </Alert>
      ) : null}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            By status
          </Typography>
          {statsLoading ? (
            <CircularProgress size={24} />
          ) : (
            <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
              <Chip label={`Total ${stats?.total ?? 0}`} color="primary" variant="outlined" />
              {STATUSES.map((s) => {
                const n = stats?.byStatus?.[s];
                if (!n) return null;
                return (
                  <Chip key={s} label={`${s}: ${n}`} size="small" color={STATUS_COLOR[s] || 'default'} variant="filled" />
                );
              })}
            </Stack>
          )}
        </CardContent>
      </Card>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Burn indexer scanner status {healthFetching ? <CircularProgress size={12} sx={{ ml: 1 }} /> : null}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {health?.indexerConfig
                ? `poll ${health.indexerConfig.burnPollMs ?? '—'}ms · chunk ${
                    health.indexerConfig.maxChunkSize ?? '—'
                  } · delay ${health.indexerConfig.chunkDelayMs ?? '—'}ms`
                : ''}
            </Typography>
          </Stack>

          {health?.chains?.length ? (
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Chain</TableCell>
                    <TableCell align="right">Tip</TableCell>
                    <TableCell align="right">Last scanned</TableCell>
                    <TableCell align="right">Lag (blocks)</TableCell>
                    <TableCell align="right">Lag (time)</TableCell>
                    <TableCell align="right">ETA to tip</TableCell>
                    <TableCell sx={{ minWidth: 140 }}>Catch-up</TableCell>
                    <TableCell align="right">Initial lookback</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(health.chains || []).map((ch) => {
                    const tip = ch.tip == null ? null : Number(ch.tip);
                    const cursor = ch.lastScannedBlock == null ? null : Number(ch.lastScannedBlock);
                    const lag = ch.lag == null ? null : Number(ch.lag);
                    const lagSeconds = ch.lagSeconds == null ? null : Number(ch.lagSeconds);
                    const chunkSize = health.indexerConfig?.maxChunkSize || 1000;
                    const chunkDelayMs = health.indexerConfig?.chunkDelayMs ?? 100;
                    const etaSec =
                      lag != null && lag > 0
                        ? Math.ceil(lag / chunkSize) * ((chunkDelayMs + 200) / 1000)
                        : 0;
                    const denom = tip ? tip : 1;
                    const progressPct =
                      cursor != null && tip != null && tip > 0
                        ? Math.max(0, Math.min(100, (cursor / denom) * 100))
                        : null;
                    const lagColor = lag == null ? 'default' : lag < 50 ? 'success' : lag < 5000 ? 'warning' : 'error';
                    return (
                      <TableRow key={ch.chainId} hover>
                        <TableCell>{chainName(ch.chainId)}</TableCell>
                        <TableCell align="right">{tip == null ? '—' : tip.toLocaleString()}</TableCell>
                        <TableCell align="right">{cursor == null ? '—' : cursor.toLocaleString()}</TableCell>
                        <TableCell align="right">
                          <Chip
                            size="small"
                            color={lagColor}
                            label={lag == null ? '—' : lag.toLocaleString()}
                            variant={lag != null && lag < 50 ? 'filled' : 'outlined'}
                          />
                        </TableCell>
                        <TableCell align="right">{formatDuration(lagSeconds)}</TableCell>
                        <TableCell align="right">{lag == null ? '—' : formatDuration(etaSec)}</TableCell>
                        <TableCell sx={{ minWidth: 140 }}>
                          {progressPct == null ? (
                            '—'
                          ) : (
                            <Tooltip title={`${progressPct.toFixed(4)}%`}>
                              <LinearProgress
                                variant="determinate"
                                value={progressPct}
                                sx={{ height: 8, borderRadius: 4 }}
                              />
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {ch.initialLookbackBlocks == null ? '—' : Number(ch.initialLookbackBlocks).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No chain cursors yet (indexer hasn't started).
            </Typography>
          )}

          <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center" sx={{ mt: 1 }}>
            <Typography variant="body2">
              mint_submitted: <strong>{health?.counters?.mint_submitted ?? '—'}</strong>
            </Typography>
            <Typography variant="body2">
              failed (1h): <strong>{health?.counters?.failed_last_hour ?? '—'}</strong>
            </Typography>
            <Typography variant="body2">
              paused: <strong>{health?.heartbeats ? String(health.heartbeats.mintingPaused) : '—'}</strong>
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
            <Typography variant="body2" component="span">
              Mint payer:
            </Typography>
            {health?.executor?.payerAddress ? (
              <ExplorerHexLink
                chainId={health.executor.chainId ?? MINT_EXECUTOR_CHAIN_ID}
                kind="address"
                raw={health.executor.payerAddress}
                iconTooltip="Relayer wallet that pays gas for V2 mint transactions"
              />
            ) : (
              <Typography variant="body2" color="text.secondary" component="span">
                {health?.executor?.error || '—'}
              </Typography>
            )}
          </Stack>
          {health?.heartbeats ? (
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
              indexer tick: {formatHeartbeatAge(health.heartbeats.lastIndexerTickAt)} · worker tick:{' '}
              {formatHeartbeatAge(health.heartbeats.lastWorkerTickAt)} · reconcile tick:{' '}
              {formatHeartbeatAge(health.heartbeats.lastReconcileTickAt)}
            </Typography>
          ) : null}

          {canMutatePipeline ? (
            <>
          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Rewind cursor (re-scan history)
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
            Moves <code>MintChainCursor.lastScannedBlock</code> backwards by N blocks for the selected chain. The burn
            indexer will re-process those blocks on its next tick. Re-detected burns are deduped against existing
            <code> MintPipelineRequest</code> rows by source-event identity.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="rewind-chain">Chain</InputLabel>
              <Select
                labelId="rewind-chain"
                label="Chain"
                value={rewindChainId}
                onChange={(e) => setRewindChainId(e.target.value)}
              >
                <MenuItem value="">
                  <em>Pick a chain</em>
                </MenuItem>
                {(health?.chains || []).map((ch) => (
                  <MenuItem key={ch.chainId} value={String(ch.chainId)}>
                    {chainName(ch.chainId)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="rewind-preset">Preset</InputLabel>
              <Select
                labelId="rewind-preset"
                label="Preset"
                value={String(rewindBlocks)}
                onChange={(e) => setRewindBlocks(Number(e.target.value))}
              >
                {REWIND_PRESETS.map((p) => (
                  <MenuItem key={p.blocks} value={String(p.blocks)}>
                    {p.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Blocks"
              type="number"
              value={rewindBlocks}
              onChange={(e) => setRewindBlocks(Math.max(1, parseInt(e.target.value, 10) || 0))}
              inputProps={{ min: 1, max: 2_000_000, step: 100 }}
              sx={{ width: 160 }}
            />
            <Button
              variant="contained"
              color="warning"
              startIcon={<HistoryIcon />}
              disabled={!rewindChainId || !rewindBlocks || rewindMutation.isPending}
              onClick={() => {
                const cid = Number(rewindChainId);
                if (
                  !window.confirm(
                    `Rewind ${chainName(cid)} cursor by ${rewindBlocks.toLocaleString()} blocks?\n\nThe burn indexer will re-scan those blocks on its next tick.`
                  )
                ) {
                  return;
                }
                rewindMutation.mutate({ chainId: cid, blocks: rewindBlocks });
              }}
            >
              {rewindMutation.isPending ? 'Rewinding…' : 'Rewind'}
            </Button>
          </Stack>
          {rewindMutation.isError ? (
            <Alert severity="error" sx={{ mt: 1 }}>
              {rewindMutation.error?.response?.data?.error || rewindMutation.error?.message || 'Rewind failed'}
            </Alert>
          ) : null}
          {rewindMutation.isSuccess && rewindMutation.data ? (
            <Alert severity="success" sx={{ mt: 1 }}>
              Rewound chain {rewindMutation.data.chainId} by {Number(rewindMutation.data.blocksRewound).toLocaleString()}{' '}
              blocks: {Number(rewindMutation.data.lastScannedBefore).toLocaleString()} →{' '}
              {Number(rewindMutation.data.lastScannedAfter).toLocaleString()}
            </Alert>
          ) : null}
            </>
          ) : null}
        </CardContent>
      </Card>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            By chain
          </Typography>
          {statsLoading || !stats?.byChain?.length ? (
            <Typography variant="body2" color="text.secondary">
              {statsLoading ? '…' : 'No rows yet'}
            </Typography>
          ) : (
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {stats.byChain.map((c) => (
                <Chip
                  key={chainLabel(c.chainId)}
                  size="small"
                  variant="outlined"
                  label={`${chainLabel(c.chainId)} · ${c.total}`}
                />
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap">
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="mpl-status-filter">Status</InputLabel>
          <Select
            labelId="mpl-status-filter"
            label="Status"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="">All</MenuItem>
            {STATUSES.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="mpl-source-filter">Source</InputLabel>
          <Select
            labelId="mpl-source-filter"
            label="Source"
            value={sourceFilter}
            onChange={(e) => {
              setSourceFilter(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="burn_migration">burn_migration</MenuItem>
            <MenuItem value="gameplay">gameplay</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="mpl-chain-filter">Chain</InputLabel>
          <Select
            labelId="mpl-chain-filter"
            label="Chain"
            value={chainFilter}
            onChange={(e) => {
              setChainFilter(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="null">No chain (gameplay)</MenuItem>
            {stats?.byChain
              ?.filter((c) => c.chainId != null)
              .map((c) => (
                <MenuItem key={String(c.chainId)} value={String(c.chainId)}>
                  {chainLabel(c.chainId)}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 280, maxWidth: 420 }}>
          <InputLabel id="mpl-contract-filter">Contract</InputLabel>
          <Select
            labelId="mpl-contract-filter"
            label="Contract"
            value={effectiveContractFilter}
            onChange={(e) => {
              setContractFilter(e.target.value);
              setPage(0);
            }}
            renderValue={(v) => (v ? v : 'All contracts')}
          >
            <MenuItem value="">
              <em>All contracts</em>
            </MenuItem>
            {contractOptions.map((addr) => (
              <MenuItem key={addr} value={addr} sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                {addr}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {contractsData?.capped ? (
          <Typography variant="caption" color="text.secondary" sx={{ width: '100%' }}>
            Contract list capped at 500 distinct addresses (sorted).
          </Typography>
        ) : null}
        <TextField
          size="small"
          label="Token ID"
          value={tokenIdFilter}
          onChange={(e) => {
            setTokenIdFilter(e.target.value);
            setPage(0);
          }}
          sx={{ width: 140 }}
        />
        {canRequeueFailed ? (
          <Tooltip
            title={
              bulkRequeueCooldown
                ? 'Bulk requeue — available again in ~20s'
                : 'Requeue every failed row on this page (respects current filters)'
            }
          >
            <span>
              <Button
                startIcon={
                  bulkRequeueMutation.isPending ? <CircularProgress size={16} color="inherit" /> : <ReplayIcon />
                }
                size="small"
                variant="outlined"
                disabled={
                  visibleRequeueableIds.length === 0 || bulkRequeueCooldown || bulkRequeueMutation.isPending
                }
                onClick={handleBulkRequeue}
              >
                Requeue all visible ({visibleRequeueableIds.length})
              </Button>
            </span>
          </Tooltip>
        ) : null}
      </Stack>

      {bulkRequeueMutation.isSuccess && bulkRequeueMutation.data ? (
        <Alert severity={bulkRequeueMutation.data.failed > 0 ? 'warning' : 'success'} sx={{ mb: 2 }} onClose={() => bulkRequeueMutation.reset()}>
          Requeued {bulkRequeueMutation.data.ok} of {bulkRequeueMutation.data.total} visible failed row(s).
          {bulkRequeueMutation.data.failed > 0
            ? ` ${bulkRequeueMutation.data.failed} could not be requeued (may have changed status).`
            : ''}
        </Alert>
      ) : null}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Created · status</TableCell>
              <TableCell>Source · chain</TableCell>
              <TableCell>Legacy · V2 NFT</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Collection · addresses · txs</TableCell>
              <TableCell>Attempts · error</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rowsLoading && rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={MINT_TABLE_COL_COUNT} align="center">
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={MINT_TABLE_COL_COUNT} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No matching rows
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                const createdShort =
                  row.createdAt != null
                    ? new Date(row.createdAt).toISOString().replace('T', ' ').slice(0, 16)
                    : '—';
                const tokenBits = [
                  row.sourceTokenId != null && String(row.sourceTokenId).trim() !== ''
                    ? `src #${row.sourceTokenId}`
                    : null,
                  row.mintedTokenId != null && String(row.mintedTokenId).trim() !== ''
                    ? `minted #${row.mintedTokenId}`
                    : null,
                  row.v2TokenId != null && String(row.v2TokenId).trim() !== '' ? `v2 #${row.v2TokenId}` : null,
                ].filter(Boolean);
                const errSnippet =
                  row.rejectReason != null && String(row.rejectReason).trim() !== ''
                    ? String(row.rejectReason).trim().length > 72
                      ? `${String(row.rejectReason).trim().slice(0, 72)}…`
                      : String(row.rejectReason).trim()
                    : null;

                return (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      <Stack spacing={0.5} sx={{ minWidth: 118 }}>
                        <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
                          {createdShort}
                        </Typography>
                        <Chip size="small" label={row.status} color={STATUS_COLOR[row.status] || 'default'} />
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      <Stack spacing={0.25}>
                        <Typography variant="caption">{row.source}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.chainId != null ? `${chainName(row.chainId)} (burn)` : 'No burn chain (gameplay)'}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      <Stack direction="row" spacing={1} alignItems="flex-start" flexWrap="wrap">
                        <Stack direction="row" spacing={0.25} alignItems="center">
                          <MetadataPreview name={row.sourceDisplayName} imageUrl={row.sourceDisplayImage} compact />
                          <SourceLegacyTokenExplorerLink
                            sourceChainId={row.chainId}
                            sourceContract={row.sourceContract}
                            sourceTokenId={row.sourceTokenId}
                          />
                        </Stack>
                        <Stack direction="row" spacing={0.25} alignItems="center">
                          <MetadataPreview name={row.v2MetadataName} imageUrl={row.v2MetadataImage} compact />
                          <V2MintedTokenExplorerLink
                            contract={row.mintedContract || row.destinationContract}
                            tokenId={row.v2TokenId || row.mintedTokenId}
                          />
                        </Stack>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top', maxWidth: 160 }}>
                      <Stack spacing={0.25}>
                        <Typography variant="caption" sx={{ lineHeight: 1.25 }}>
                          {row.userName || '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.25, wordBreak: 'break-word' }}>
                          {row.userEmail || (row.userId ? formatCell(row.userId) : '—')}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top', maxWidth: 300 }}>
                      <Stack spacing={0.5}>
                        {row.destinationCollection ? (
                          <Tooltip title={String(row.destinationCollection)}>
                            <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.25 }} noWrap>
                              {formatCell(row.destinationCollection)}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            —
                          </Typography>
                        )}
                        <Stack spacing={0.5}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                            V2 mint · Avalanche
                          </Typography>
                          <Stack spacing={0.35} sx={{ pl: 0.5 }}>
                            <ExplorerHexLink
                              chainId={MINT_EXECUTOR_CHAIN_ID}
                              kind="address"
                              raw={row.destinationContract}
                              label="Dest"
                              iconTooltip="Destination contract on Avalanche"
                            />
                            <ExplorerHexLink
                              chainId={MINT_EXECUTOR_CHAIN_ID}
                              kind="address"
                              raw={row.mintedContract}
                              label="Minted"
                              iconTooltip="Minted contract on Avalanche"
                            />
                            <ExplorerHexLink
                              chainId={MINT_EXECUTOR_CHAIN_ID}
                              kind="address"
                              raw={row.recipient}
                              label="Recipient"
                              iconTooltip="Recipient wallet on Avalanche"
                            />
                            <ExplorerHexLink
                              chainId={MINT_EXECUTOR_CHAIN_ID}
                              kind="tx"
                              raw={row.v2TxHash}
                              label="Mint tx"
                              iconTooltip="Mint transaction on Avalanche"
                            />
                          </Stack>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, pt: 0.25 }}>
                            Legacy burn · {row.chainId != null ? chainName(row.chainId) : '—'}
                          </Typography>
                          <Stack spacing={0.35} sx={{ pl: 0.5 }}>
                            <ExplorerHexLink
                              chainId={row.chainId}
                              kind="address"
                              raw={row.sourceContract}
                              label="Src NFT"
                              iconTooltip="Source NFT contract on burn chain"
                            />
                            <ExplorerHexLink
                              chainId={row.chainId}
                              kind="tx"
                              raw={row.txHash}
                              label="Burn tx"
                              iconTooltip="Burn transaction on source chain"
                            />
                          </Stack>
                        </Stack>
                        {tokenBits.length ? (
                          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                            {tokenBits.join(' · ')}
                          </Typography>
                        ) : null}
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top', maxWidth: 200 }}>
                      <Stack spacing={0.35}>
                        <Typography variant="caption">{row.attemptCount ?? '—'}</Typography>
                        {errSnippet ? (
                          <Tooltip title={String(row.rejectReason).trim()}>
                            <Typography variant="caption" color="error" sx={{ display: 'block', lineHeight: 1.25 }}>
                              {errSnippet}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                      {(() => {
                        const canClearLegacy =
                          row.source === 'burn_migration' &&
                          row.sourceContract != null &&
                          String(row.sourceContract).trim() !== '' &&
                          row.sourceTokenId != null &&
                          String(row.sourceTokenId).trim() !== '';
                        const canRequeue = canRequeueFailed && row.status === 'failed';
                        if (!canMutatePipeline && !canRequeue) {
                          return '—';
                        }
                        return (
                          <Stack direction="row" spacing={0.25} justifyContent="flex-end" alignItems="center" flexWrap="wrap">
                            {canMutatePipeline && canClearLegacy ? (
                              <Tooltip title="Clear legacy metadata cache (IMX packed + legacy id). Server re-fetches on next resolve.">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="warning"
                                    disabled={clearLegacyCacheMutation.isPending}
                                    onClick={() => {
                                      if (
                                        !window.confirm(
                                          'Delete LegacyMetadataCaches for this source NFT (all id variants)? Next metadata resolve will re-hydrate from chain.'
                                        )
                                      ) {
                                        return;
                                      }
                                      clearLegacyCacheMutation.mutate(row.id);
                                    }}
                                  >
                                    <LayersClearIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            ) : null}
                            {canRequeue ? (
                              <Tooltip
                                title={
                                  requeueCooldownIds[row.id]
                                    ? 'Requeued — available again in ~20s'
                                    : 'Requeue (failed → eligible)'
                                }
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={isRequeueDisabled(row.id)}
                                    onClick={() => handleRequeue(row.id)}
                                  >
                                    <ReplayIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            ) : null}
                          </Stack>
                        );
                      })()}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={pagination?.total ?? 0}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 15, 25, 50]}
        />
      </TableContainer>
    </Box>
  );
}
